import { useDropZone } from '@vueuse/core';
import { EFileUploadError } from 'common/enum/file.upload.enum';
import { isFileSizeExceedingLimit, isFileTypeAllowed, type TFileUploadError } from 'common/utils/file.utils';
import { computed, type ComputedRef, type Ref, type ShallowRef } from 'vue';

interface IFileDropZoneOptions {
  dropZoneRef: Readonly<ShallowRef<HTMLElement | null>>;
  fileInputRef: Readonly<ShallowRef<HTMLInputElement | null>>;
  /** MIME types or filename extensions, matching the native file input's accept attribute. */
  allowedFileTypes: string[];
  /** Omit to accept any size, e.g. when the limit is enforced elsewhere */
  maxSizeBytes?: number;
  onFile: (file: File) => void | Promise<void>;
  onError?: (error: NonNullable<TFileUploadError>) => void;
  /** While this returns true no file is taken in: drops are ignored, clicks call onBlockedIntent */
  isBlocked?: () => boolean;
  onBlockedIntent?: () => void;
}

interface IFileDropZoneComposable {
  accept: ComputedRef<string>;
  isOverDropZone: Ref<boolean>;
  openFileExplorer: () => void;
  handleFileChange: (event: Event) => void;
}

/**
 * Shared picking behaviour for file upload widgets: hidden file input, drag & drop, and
 * type/size validation. Each widget keeps its own markup — only the mechanics live here.
 */
export const useFileDropZone = (options: IFileDropZoneOptions): IFileDropZoneComposable => {
  const { dropZoneRef, fileInputRef, allowedFileTypes, maxSizeBytes, onFile, onError, isBlocked, onBlockedIntent } =
    options;

  const accept = computed<string>(() => allowedFileTypes.join(','));
  const allowedExtensions = allowedFileTypes.filter((type) => type.startsWith('.'));

  const takeFile = (files: File[] | FileList | null): void => {
    if (!files?.length || isBlocked?.()) return;

    const file = files[0];
    const matchesExtension = allowedExtensions.some((extension) =>
      file.name.toLowerCase().endsWith(extension.toLowerCase()),
    );
    if (files.length !== 1 || (!matchesExtension && !isFileTypeAllowed(file.type, allowedFileTypes))) {
      onError?.(EFileUploadError.TYPE);
      return;
    }
    if (maxSizeBytes !== undefined && isFileSizeExceedingLimit(file.size, maxSizeBytes)) {
      onError?.(EFileUploadError.SIZE);
      return;
    }

    void onFile(file);
  };

  const openFileExplorer = (): void => {
    if (isBlocked?.()) {
      onBlockedIntent?.();
      return;
    }
    fileInputRef.value?.click();
  };

  const handleFileChange = (event: Event): void => {
    const input = event.target as HTMLInputElement;
    takeFile(input.files);

    // Reset so selecting the same file again still triggers a change event.
    input.value = '';
  };

  const { isOverDropZone } = useDropZone(dropZoneRef, {
    onDrop: takeFile,
    // Drag metadata exposes MIME types only; validate extension-based uploads when the files are dropped.
    dataTypes: allowedExtensions.length ? undefined : allowedFileTypes,
    // Keep the full list so takeFile can reject multiple files instead of silently taking the first.
    multiple: true,
    preventDefaultForUnhandled: false,
  });

  return {
    accept,
    isOverDropZone,
    openFileExplorer,
    handleFileChange,
  };
};
